import React from "react";
import { Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import "./statusPage.scss";

// Generic, reusable status page. Used by NotFound (404) and Forbidden (403); add
// more wrappers (e.g. ServiceUnavailable for 503) by passing a different `code`,
// `title`, `Icon`, and `tone`.
//
// Props:
//   code          — the big numeric label ("404", "403", …)
//   tone          — "info" | "warning" | "error" | "muted" — drives the badge tint
//   Icon          — a lucide-react icon component
//   title         — short bold lead-in
//   message       — single sentence, plain language. No technical jargon.
//   primaryAction — { label, onClick } — defaults to "Go to dashboard"
//   secondaryAction — { label, onClick } — defaults to "Go back"
//   showPath      — if true (default), shows the offending request path so the
//                   user can see what they tried to load. Useful for 404, less so
//                   for 403 where the path is sensitive.

const StatusPage = ({
  code,
  tone = "muted",
  Icon,
  title,
  message,
  primaryAction,
  secondaryAction,
  showPath = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const onPrimary = primaryAction?.onClick || (() => navigate("/schedules"));
  const onSecondary = secondaryAction?.onClick || (() => navigate(-1));

  return (
    <div className="status-page" role="alert" aria-live="polite">
      <div className="status-page__card">
        {Icon && (
          <div className={`status-page__badge status-page__badge--${tone}`}>
            <Icon size={32} aria-hidden="true" />
          </div>
        )}
        <p className="status-page__code" aria-label={`Status code ${code}`}>
          {code}
        </p>
        <h1 className="status-page__title">{title}</h1>
        <p className="status-page__message">{message}</p>
        <div className="status-page__actions">
          <Button variant="primary" onClick={onPrimary}>
            {primaryAction?.label || "Go to dashboard"}
          </Button>
          <Button variant="outline-secondary" onClick={onSecondary}>
            {secondaryAction?.label || "Go back"}
          </Button>
        </div>
        {showPath && location?.pathname && (
          <div className="status-page__path">
            Requested: <code>{location.pathname}</code>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusPage;
